import dayjs from 'dayjs';
import Image from 'next/image';
import { GetServerSideProps } from 'next';
import { ArticleJsonLd, NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { Suspense, useEffect, useState } from 'react';
import { Container } from 'src/components/container';
import { WafFriendlyPass } from 'src/types/waf-friendly-pass';
import { formatDate } from 'src/utils/dayjs/formatDate';
import { Meta } from 'src/utils/meta';
import { Routes } from 'src/utils/routes';
import { wpApiUrl } from 'src/utils/wpApiUrl';
import { Breadcrumbs } from 'src/components/breadcrumb';

import styles from './style.module.scss';
import { CtaBanner } from 'src/components/ctaBanner';
import { fetchWithCache } from 'src/utils/fetchWithCache';
import { isStaging } from 'src/utils/isStaging';
import dynamic from 'next/dynamic';
import { SkeletonLoader } from 'src/components/skeleton';

const DynamicNewsCard = dynamic(() => import("src/components/newsCard"), { suspense: true })

type Props = {
  post: WafFriendlyPass.news
  otherItemList: WafFriendlyPass.news[],
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const id = context?.params?.newsId
  const limit = 3
  const post = await fetchWithCache(`${wpApiUrl.news}/${id}`) as Props['post']
  const otherItemList = await fetchWithCache(`${wpApiUrl.news}?per_page=${limit}`) as Props['otherItemList']
  return { props: { post, otherItemList } }
}

const News = ({ post, otherItemList }: Props) => {
  const [loadOnce, setLoadOnce] = useState(false)
  const [otherNews, setOtherNews] = useState<WafFriendlyPass.news[]>([])
  const router = useRouter()
  const firstLoad = async () => {
    setOtherNews(isStaging ? otherItemList : otherItemList.filter((item) => item.acf.status))
  }
  useEffect(() => {
    if (loadOnce) {
      return
    }
    firstLoad()
    setLoadOnce(true)
  }, [firstLoad, otherNews])

  const breadcrumbs = [
    { name: 'トップ', href: Routes.HOME },
    { name: 'お知らせ', href: '/news/page/1/' },
    { name: post.title.rendered },
  ]

  return (
    <div>
      <NextSeo
        title={`${post.title.rendered} | ${Meta.TITLE}`}
        openGraph={{
          url: `https://pass.workation-fukuoka.jp${router.asPath}`,
          title: `${post.title.rendered} | ${Meta.TITLE}`,
          images: [
            {
              url: `${post.acf.mainImage ? post.acf.mainImage.url : Meta.OGP_IMAGE}`,
              width: 1200,
              height: 630,
              alt: `${post.title} | ${Meta.TITLE}`,
            },
          ],
        }}
      />
      <ArticleJsonLd
        url={`https://pass.workation-fukuoka.jp${router.asPath}`}
        title={post.title.rendered}
        images={post.acf.mainImage ? post.acf.mainImage.url : Meta.OGP_IMAGE}
        datePublished={formatDate(dayjs(post.date).toDate())}
        dateModified={formatDate(dayjs(post.modified).toDate())}
        authorName="W@F事務局"
        description={Meta.DESCRIPTION}
      />
      <Container className={styles.news}>
        <div className={styles.newsContainer}>
          {post && (
            <div>
              <time className={styles.newsCardDate} dateTime={post.modified}>
                {formatDate(dayjs(post.modified).toDate())}
              </time>

              <h1 className={styles.newsHeading}>{post.title.rendered}</h1>

              {!!post.acf.mainImage && (
                <div className={styles.newsMainImage}>
                  <Image
                    src={post.acf.mainImage.sizes.large}
                    layout="responsive"
                    width={800}
                    height={500}
                    alt={post.acf.mainImage.title}
                  />
                </div>
              )}

              <div
                className={styles.newsContent}
                dangerouslySetInnerHTML={{ __html: post.content.rendered }}
              />

            </div>
          )}

          {
            otherItemList &&
              otherItemList.length
              ?
              (
                <div className={styles.newsOther}>
                  <h2 className={styles.newsOtherHeading}>その他のお知らせ</h2>
                  <div className={styles.newsOtherList}>
                    <Suspense fallback={<SkeletonLoader count={3} />}>
                      {
                        otherItemList.map((item, idx) => (
                          <DynamicNewsCard
                            key={idx}
                            item={item}
                          />
                        ))
                      }
                    </Suspense>
                  </div>
                </div>
              )
              : false
          }
        </div>

        <div className={styles.newsCtaBanner}>
          <CtaBanner />
        </div>

      </Container>

      <Breadcrumbs items={breadcrumbs} />

    </div>
  )
}

export default News
